import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUploadCloud, FiDownload, FiPrinter, FiDatabase, FiLayout, FiImage, FiLink, FiType } = FiIcons;

const GOOGLE_FONTS = ['Oswald', 'Inter', 'Roboto', 'Montserrat', 'Open Sans', 'Playfair Display', 'Caveat', 'Rubik', 'PT Sans', 'Lora', 'Comfortaa', 'Russo One'];

export default function SettingsPanel({
  textInput,
  setTextInput,
  options,
  setOptions,
  parsedData,
  onExportZip,
  onExportPdf,
  isGenerating
}) {
  const parsedCount = parsedData.length;

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setOptions({ ...options, logoUrl: url });
    }
  };

  const handleFontUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      const name = file.name.split('.')[0].replace(/[^a-zA-Z0-9]/g, '');
      setOptions({ 
        ...options, 
        fontFamily: 'Custom', 
        customFontUrl: url, 
        customFontName: name || 'CustomFont' 
      });
    }
  };

  const ColorPicker = ({ label, value, onChange }) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-textMuted uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-3 bg-inputBg p-2 rounded-lg border border-gray-800">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0"
        />
        <span className="text-sm text-gray-300 font-mono uppercase">{value}</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      
      {/* Секция 1: Данные */}
      <div className="bg-panelBg rounded-xl border border-gray-800 overflow-hidden">
        <div className="bg-panelCard p-4 border-b border-gray-800 flex items-center gap-2">
          <SafeIcon icon={FiDatabase} className="text-accent" />
          <h2 className="font-bold text-sm tracking-widest uppercase">Система & Данные</h2>
        </div>
        <div className="p-5">
          <textarea
            className="w-full h-24 p-3 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent focus:border-accent transition-all resize-none text-sm font-mono text-gray-300 outline-none"
            placeholder="Вставьте ссылки сюда..."
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
          />
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-textMuted">Можно вставлять сплошным текстом</span>
            <div className="bg-[#2a2a2a] px-3 py-1 rounded text-xs font-bold text-accent">
              Найдено: {parsedCount}
            </div>
          </div>
        </div>
      </div>

      {/* Секция 2: Макет и Текст */}
      <div className="bg-panelBg rounded-xl border border-gray-800 overflow-hidden">
        <div className="bg-panelCard p-4 border-b border-gray-800 flex items-center gap-2">
          <SafeIcon icon={FiLayout} className="text-accent" />
          <h2 className="font-bold text-sm tracking-widest uppercase">Формат & Текст</h2>
        </div>
        <div className="p-5 flex flex-col gap-5">
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-inputBg p-4 rounded-lg border border-gray-800">
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Ширина (мм)</label>
              <input
                type="number" min="10" max="200"
                value={options.stickerWidth}
                onChange={(e) => setOptions({ ...options, stickerWidth: parseInt(e.target.value) || 50 })}
                className="w-full p-2.5 bg-panelBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Высота (мм)</label>
              <input
                type="number" min="10" max="290"
                value={options.stickerHeight}
                onChange={(e) => setOptions({ ...options, stickerHeight: parseInt(e.target.value) || 50 })}
                className="w-full p-2.5 bg-panelBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">
                Скругление углов стикера ({options.stickerRadius} мм)
              </label>
              <input
                type="range" min="0" max="50"
                value={options.stickerRadius}
                onChange={(e) => setOptions({ ...options, stickerRadius: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FFD700] mt-3"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2 flex items-center gap-1">
                <SafeIcon icon={FiType} /> Шрифт (Кириллица)
              </label>
              <div className="flex gap-2">
                <select
                  value={options.fontFamily === 'Custom' ? 'Custom' : options.fontFamily}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val !== 'Custom') setOptions({ ...options, fontFamily: val });
                  }}
                  className="flex-1 p-2.5 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
                >
                  <option value="sans-serif">Стандартный</option>
                  {GOOGLE_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                  <option value="Custom">Свой файл...</option>
                </select>
                {options.fontFamily === 'Custom' && (
                  <label className="flex-shrink-0 flex items-center justify-center px-4 bg-inputBg border border-gray-700 rounded-lg cursor-pointer hover:border-accent text-accent text-sm font-bold">
                    Загрузить
                    <input type="file" className="hidden" accept=".ttf,.woff,.woff2,.otf" onChange={handleFontUpload} />
                  </label>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Размер шрифта</label>
              <input
                type="number" min="10" max="150"
                value={options.fontSize}
                onChange={(e) => setOptions({ ...options, fontSize: parseInt(e.target.value) || 46 })}
                className="w-full p-2.5 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>
          </div>

          <div className="grid gap-4 bg-inputBg p-4 rounded-lg border border-gray-800">
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2 text-accent">Текст НАД QR</label>
              <input
                type="text" value={options.topTemplate}
                onChange={(e) => setOptions({ ...options, topTemplate: e.target.value })}
                placeholder="Закажи еду!"
                className="w-full p-2.5 bg-panelBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2 text-accent">Текст ПОД QR</label>
              <input
                type="text" value={options.bottomTemplate}
                onChange={(e) => setOptions({ ...options, bottomTemplate: e.target.value })}
                placeholder="Ряд {row} | Место {seat}"
                className="w-full p-2.5 bg-panelBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Секция 3: Дизайн и Цвета */}
      <div className="bg-panelBg rounded-xl border border-gray-800 overflow-hidden">
        <div className="bg-panelCard p-4 border-b border-gray-800 flex items-center gap-2">
          <SafeIcon icon={FiImage} className="text-accent" />
          <h2 className="font-bold text-sm tracking-widest uppercase">Дизайн, QR & Логотип</h2>
        </div>
        <div className="p-5 flex flex-col gap-6">
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ColorPicker label="Фон стикера" value={options.bgColor} onChange={(v) => setOptions({...options, bgColor: v})} />
            <ColorPicker label="Цвет текста" value={options.textColor} onChange={(v) => setOptions({...options, textColor: v})} />
            <ColorPicker label="Цвет QR-кода" value={options.qrColor} onChange={(v) => setOptions({...options, qrColor: v})} />
            <ColorPicker label="Фон QR-кода" value={options.qrBgColor} onChange={(v) => setOptions({...options, qrBgColor: v})} />
          </div>

          <div className="border-t border-gray-800 pt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Настройки QR */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Стиль точек QR</label>
                <select
                  value={options.dotsStyle}
                  onChange={(e) => setOptions({ ...options, dotsStyle: e.target.value })}
                  className="w-full p-2.5 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
                >
                  <option value="dots">Точки</option>
                  <option value="square">Квадратные</option>
                  <option value="rounded">Скругленные</option>
                  <option value="classy">Классические</option>
                  <option value="extra-rounded">Сильно скругленные</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Логотип (URL / Файл)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiLink} className="text-gray-500" />
                    </div>
                    <input
                      type="text" value={options.logoUrl || ''}
                      onChange={(e) => setOptions({ ...options, logoUrl: e.target.value })}
                      placeholder="https://..."
                      className="w-full pl-9 p-2.5 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
                    />
                  </div>
                  <label className="flex-shrink-0 flex items-center justify-center w-[42px] h-[42px] bg-inputBg border border-gray-700 rounded-lg cursor-pointer hover:border-accent text-gray-400">
                    <SafeIcon icon={FiUploadCloud} />
                    <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                  </label>
                </div>
              </div>
            </div>

            {/* Настройки подложки логотипа */}
            <div className="space-y-4 bg-inputBg p-4 rounded-lg border border-gray-800">
              <div className="flex justify-between items-center">
                <ColorPicker label="Фон подложки лого" value={options.logoBgColor} onChange={(v) => setOptions({...options, logoBgColor: v})} />
                <div className="text-right">
                  <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Размер лого ({options.logoSize}%)</label>
                  <input
                    type="range" min="10" max="50"
                    value={options.logoSize}
                    onChange={(e) => setOptions({ ...options, logoSize: parseInt(e.target.value) })}
                    className="w-24 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FFD700]"
                  />
                </div>
              </div>
              
              <div className="pt-2">
                <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">
                  Прозрачность подложки ({options.logoBgOpacity}%)
                </label>
                <input
                  type="range" min="0" max="100"
                  value={options.logoBgOpacity}
                  onChange={(e) => setOptions({ ...options, logoBgOpacity: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FFD700]"
                />
              </div>

              <div className="pt-2">
                <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">
                  Форма подложки: {options.logoBgRadius === 50 ? 'Круг' : options.logoBgRadius === 0 ? 'Квадрат' : `Скругление ${options.logoBgRadius}%`}
                </label>
                <input
                  type="range" min="0" max="50"
                  value={options.logoBgRadius}
                  onChange={(e) => setOptions({ ...options, logoBgRadius: parseInt(e.target.value) })}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#FFD700]"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <button
          onClick={onExportZip}
          disabled={parsedCount === 0 || isGenerating}
          className="flex-1 flex items-center justify-center gap-2 bg-panelCard border border-gray-700 hover:border-accent hover:text-accent disabled:opacity-50 text-white py-3.5 px-4 rounded-xl font-bold tracking-wide transition-all uppercase text-sm"
        >
          <SafeIcon icon={FiDownload} /> Скачать ZIP
        </button>
        <button
          onClick={onExportPdf}
          disabled={parsedCount === 0 || isGenerating}
          className="flex-1 flex items-center justify-center gap-2 bg-accent hover:bg-accentHover disabled:bg-gray-700 disabled:text-gray-500 text-black py-3.5 px-4 rounded-xl font-bold tracking-wide transition-all uppercase text-sm"
        >
          <SafeIcon icon={FiPrinter} /> PDF для печати
        </button>
      </div>
      
    </div>
  );
}